export interface StoredDocument {
  id?: number;
  documentId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: Date;
  originalFile?: Blob;
  originalMarkdown?: string;
  editedMarkdown?: string;
  exportedPDF?: Blob;
  lastModified: Date;
  metadata?: {
    processingStatus?: string;
    errorMessage?: string;
    [key: string]: any;
  };
}

// DAG-003 retention: CVs and exported PDFs are governed personal data.
// Anything older than this TTL is purged on every app init so a device
// that's been idle for a week doesn't keep redacted CVs around.
const RETENTION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

class IndexedDBService {
  private dbName = 'CVRedactorDB';
  private dbVersion = 1;
  private storeName = 'documents';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'id',
            autoIncrement: true
          });

          objectStore.createIndex('documentId', 'documentId', { unique: true });
          objectStore.createIndex('fileName', 'fileName', { unique: false });
          objectStore.createIndex('uploadedAt', 'uploadedAt', { unique: false });
          objectStore.createIndex('lastModified', 'lastModified', { unique: false });
        }
      };
    });

    // DAG-003: sweep expired CV artifacts every time the app boots.
    // Errors here are non-fatal — the store stays usable for fresh
    // documents.
    try {
      const purged = await this.purgeExpired();
      if (purged > 0) {
        console.info(`CV redactor: purged ${purged} expired document(s) from IndexedDB`);
      }
    } catch (error) {
      console.warn('CV redactor: retention sweep failed (non-fatal):', error);
    }
  }

  /** Delete every stored document whose `lastModified` is older than the TTL.
   *
   * Returns the number of documents purged. Used both at startup (via
   * ``init``) and as a manual purge surface for the UI if we ever add a
   * "clear local copies" button.
   */
  async purgeExpired(now: Date = new Date()): Promise<number> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const cutoffMs = now.getTime() - RETENTION_TTL_MS;

    return new Promise((resolve, reject) => {
      const cursorRequest = store.openCursor();
      let purgedCount = 0;
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (!cursor) {
          resolve(purgedCount);
          return;
        }
        const doc: StoredDocument = cursor.value;
        const last = doc.lastModified ? new Date(doc.lastModified).getTime() : 0;
        if (last < cutoffMs) {
          cursor.delete();
          purgedCount++;
        }
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize database');
    }
    return this.db;
  }

  async saveDocument(document: StoredDocument): Promise<number> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const existingDocQuery = store.index('documentId').get(document.documentId);
      
      existingDocQuery.onsuccess = () => {
        const existingDoc = existingDocQuery.result;
        
        if (existingDoc) {
          const updatedDoc = {
            ...existingDoc,
            ...document,
            id: existingDoc.id,
            lastModified: new Date()
          };
          
          const updateRequest = store.put(updatedDoc);
          updateRequest.onsuccess = () => resolve(updateRequest.result as number);
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          document.lastModified = new Date();
          const addRequest = store.add(document);
          addRequest.onsuccess = () => resolve(addRequest.result as number);
          addRequest.onerror = () => reject(addRequest.error);
        }
      };
      
      existingDocQuery.onerror = () => reject(existingDocQuery.error);
    });
  }

  async getDocument(documentId: string): Promise<StoredDocument | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('documentId');

    return new Promise((resolve, reject) => {
      const request = index.get(documentId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getDocumentById(id: number): Promise<StoredDocument | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllDocuments(page: number = 1, pageSize: number = 5): Promise<{
    documents: StoredDocument[];
    total: number;
    pages: number;
  }> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('lastModified');

    return new Promise((resolve, reject) => {
      const countRequest = store.count();
      
      countRequest.onsuccess = () => {
        const total = countRequest.result;
        const pages = Math.ceil(total / pageSize);
        
        const documents: StoredDocument[] = [];
        let skipped = 0;
        const skip = (page - 1) * pageSize;
        
        const cursorRequest = index.openCursor(null, 'prev');
        
        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          
          if (cursor && documents.length < pageSize) {
            if (skipped < skip) {
              skipped++;
              cursor.continue();
            } else {
              documents.push(cursor.value);
              cursor.continue();
            }
          } else {
            resolve({ documents, total, pages });
          }
        };
        
        cursorRequest.onerror = () => reject(cursorRequest.error);
      };
      
      countRequest.onerror = () => reject(countRequest.error);
    });
  }

  async updateOriginalMarkdown(documentId: string, markdown: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('documentId');

    return new Promise((resolve, reject) => {
      const request = index.get(documentId);
      
      request.onsuccess = () => {
        const document = request.result;
        if (document) {
          document.originalMarkdown = markdown;
          document.lastModified = new Date();
          
          const updateRequest = store.put(document);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Document not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateEditedMarkdown(documentId: string, markdown: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('documentId');

    return new Promise((resolve, reject) => {
      const request = index.get(documentId);
      
      request.onsuccess = () => {
        const document = request.result;
        if (document) {
          document.editedMarkdown = markdown;
          document.lastModified = new Date();
          
          const updateRequest = store.put(document);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Document not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateExportedPDF(documentId: string, pdfBlob: Blob): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('documentId');

    return new Promise((resolve, reject) => {
      const request = index.get(documentId);
      
      request.onsuccess = () => {
        const document = request.result;
        if (document) {
          document.exportedPDF = pdfBlob;
          document.lastModified = new Date();
          
          const updateRequest = store.put(document);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Document not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async deleteDocument(documentId: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('documentId');

    return new Promise((resolve, reject) => {
      const request = index.getKey(documentId);
      
      request.onsuccess = () => {
        const key = request.result;
        if (key) {
          const deleteRequest = store.delete(key);
          deleteRequest.onsuccess = () => resolve();
          deleteRequest.onerror = () => reject(deleteRequest.error);
        } else {
          reject(new Error('Document not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll(): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const indexedDBService = new IndexedDBService();